import { useRouter } from "next/router"
import React, { FC, useCallback, useContext, useEffect, useState } from "react"
import { NotificationVariant } from "@aistihealth/web-common/src/components/notification/Notification"

export interface Notification {
  message: string
  variant: NotificationVariant
  type?: string
}

export interface NotificationContextValue {
  clearNotifications: () => void
  notifications: Notification[]
  persistNotificationsForOneRouteChange: () => void
  /**
   * Push a notification into the queue.
   *
   * If `type` is set, the possible existing notification of the same type will be cleared.
   */
  pushNotification: (item: Notification) => void
}

const createNotInitializedError = () => () => {
  throw new Error(
    `NotificationContext was accessed, but no provider is present.`,
  )
}

const NotificationContext = React.createContext<NotificationContextValue>({
  clearNotifications: createNotInitializedError(),
  notifications: [],
  persistNotificationsForOneRouteChange: createNotInitializedError(),
  pushNotification: createNotInitializedError(),
})

export const NotificationProvider: FC = ({ children }) => {
  const router = useRouter()

  const [notifications, setNotifications] = useState<Notification[]>([])
  const [persistNotifications, setPersistNotifications] = useState(false)

  const clearNotifications = useCallback<
    NotificationContextValue["clearNotifications"]
  >(() => {
    setNotifications([])
  }, [])

  const pushNotification = useCallback<
    NotificationContextValue["pushNotification"]
  >(item => {
    setNotifications(prevState => {
      if (item.type === undefined) {
        return prevState.concat(item)
      }

      return prevState
        .filter(newItem => newItem.type !== item.type)
        .concat(item)
    })
  }, [])

  const persistNotificationsForOneRouteChange = useCallback<
    NotificationContextValue["persistNotificationsForOneRouteChange"]
  >(() => {
    setPersistNotifications(true)
  }, [])

  useEffect(() => {
    const handleRouteChangeComplete = () => {
      setPersistNotifications(false)

      if (!persistNotifications) {
        setNotifications([])
      }
    }

    router.events.on("routeChangeComplete", handleRouteChangeComplete)

    return () => {
      router.events.off("routeChangeComplete", handleRouteChangeComplete)
    }
  }, [router.events, persistNotifications])

  return (
    <NotificationContext.Provider
      value={{
        clearNotifications,
        notifications,
        persistNotificationsForOneRouteChange,
        pushNotification,
      }}
    >
      {children}
    </NotificationContext.Provider>
  )
}

export const useNotifications = (): NotificationContextValue =>
  useContext(NotificationContext)
